import { apiClient } from '@services/api-client';

// ===== Types =====

export interface CapabilityParameter {
  name: string;
  type: 'string' | 'integer' | 'number' | 'boolean' | 'array' | 'object';
  description: string;
  required: boolean;
  default?: unknown;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  maxLength?: number;
}

export interface Capability {
  id: string;
  name: string;
  description: string;
  category: string;
  version: string;
  status: 'active' | 'disabled' | 'deprecated' | 'beta';
  parameters: CapabilityParameter[];
  tags: string[];
  is_builtin: boolean;
  tenant_id?: string;
  connector_id?: string;
  connector_action?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CapabilityCatalog {
  capabilities: Record<string, Capability[]>;
  categories: string[];
  total_count: number;
}

export interface CreateCapabilityDto {
  id: string;
  name: string;
  description: string;
  category?: string;
  parameters?: Omit<CapabilityParameter, 'name' | 'type' | 'description' | 'required'> & {
    name: string;
    type?: string;
    description?: string;
    required?: boolean;
  }[];
  connector_id?: string;
  connector_action?: string;
  connector_config?: Record<string, unknown>;
  tags?: string[];
  rate_limit?: number;
}

export interface UpdateCapabilityDto {
  name?: string;
  description?: string;
  parameters?: CapabilityParameter[];
  connector_config?: Record<string, unknown>;
  tags?: string[];
  status?: 'active' | 'disabled' | 'deprecated' | 'beta';
  rate_limit?: number;
}

export interface ExecuteCapabilityDto {
  parameters: Record<string, unknown>;
  session_id?: string;
  // Set when triggered manually from a ticket's "Run capability" button —
  // the BFF turns this into metadata.invocation_source='agent_manual' for
  // the mcp-server audit trail.
  ticketId?: string;
}

export interface ExecutionResult {
  success: boolean;
  data?: Record<string, unknown>;
  error?: string;
  error_code?: string;
  execution_time_ms?: number;
}

export interface ImportOpenAPIDto {
  url?: string;
  spec?: Record<string, unknown>;
  connector_id?: string;
  prefix?: string;
  tags_filter?: string[];
}

export interface ImportYAMLDto {
  content: string;
  connector_id?: string;
}

export interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  errors: string[];
  capabilities: Capability[];
}

// ===== Service =====

const MCP_API_BASE = '/mcp/v1/capabilities';

class IntegrationsService {
  // Get capability catalog
  async getCatalog(category?: string, includeCustom = true): Promise<CapabilityCatalog> {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (!includeCustom) params.set('include_custom', 'false');

    const response = await apiClient.get<CapabilityCatalog>(
      `${MCP_API_BASE}/catalog?${params.toString()}`
    );
    return response.data;
  }

  // List capabilities
  async listCapabilities(filters?: {
    category?: string;
    status?: string;
    tags?: string;
  }): Promise<Capability[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.set('category', filters.category);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.tags) params.set('tags', filters.tags);

    const response = await apiClient.get<Capability[]>(
      `${MCP_API_BASE}?${params.toString()}`
    );
    return response.data;
  }

  // Get single capability
  async getCapability(capabilityId: string): Promise<Capability> {
    const response = await apiClient.get<Capability>(
      `${MCP_API_BASE}/${capabilityId}`
    );
    return response.data;
  }

  // Create capability
  async createCapability(data: CreateCapabilityDto): Promise<{ success: boolean; capability: Capability }> {
    const response = await apiClient.post<{ success: boolean; capability: Capability }>(
      MCP_API_BASE,
      data
    );
    return response.data;
  }

  // Update capability
  async updateCapability(
    capabilityId: string,
    data: UpdateCapabilityDto
  ): Promise<{ success: boolean; capability: Capability }> {
    const response = await apiClient.patch<{ success: boolean; capability: Capability }>(
      `${MCP_API_BASE}/${capabilityId}`,
      data
    );
    return response.data;
  }

  // Delete capability
  async deleteCapability(capabilityId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(
      `${MCP_API_BASE}/${capabilityId}`
    );
    return response.data;
  }

  // Execute capability
  async executeCapability(
    capabilityId: string,
    data: ExecuteCapabilityDto
  ): Promise<ExecutionResult> {
    const response = await apiClient.post<ExecutionResult>(
      `${MCP_API_BASE}/${capabilityId}/execute`,
      data
    );
    return response.data;
  }

  // Test capability connector
  async testCapability(capabilityId: string): Promise<{
    success: boolean;
    connector_id?: string;
    message: string;
    error?: string;
  }> {
    const response = await apiClient.post(`${MCP_API_BASE}/${capabilityId}/test`);
    return response.data;
  }

  // Get MCP schema
  async getMcpSchema(capabilityId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`${MCP_API_BASE}/${capabilityId}/mcp-schema`);
    return response.data;
  }

  // Get OpenAI function schema
  async getOpenAISchema(capabilityId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`${MCP_API_BASE}/${capabilityId}/openai-schema`);
    return response.data;
  }

  // Import from OpenAPI
  async importFromOpenAPI(data: ImportOpenAPIDto): Promise<ImportResult> {
    const response = await apiClient.post<ImportResult>(
      `${MCP_API_BASE}/import/openapi`,
      data
    );
    return response.data;
  }

  // Import from YAML
  async importFromYAML(data: ImportYAMLDto): Promise<ImportResult> {
    const response = await apiClient.post<ImportResult>(
      `${MCP_API_BASE}/import/yaml`,
      data
    );
    return response.data;
  }

  // Import from file
  async importFromFile(
    file: File,
    options?: { connector_id?: string; prefix?: string }
  ): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.connector_id) {
      formData.append('connector_id', options.connector_id);
    }
    if (options?.prefix) {
      formData.append('prefix', options.prefix);
    }

    const response = await apiClient.post<ImportResult>(
      `${MCP_API_BASE}/import/file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }
}

export const integrationsService = new IntegrationsService();
